import jApi, {
    WidgetDisplayMode,
    WidgetPresenceResponse,
    WidgetPresenceResponseUserList,
    WidgetPresenceType,
} from 'jamespot-user-api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { Toast, updateWidgetContent, Widget } from 'jamespot-front-business';
import { useWidgetRT } from '../hooks';
import { WidgetFooterView } from './components/WidgetFooterView';
import { WidgetPresenceWrapperClosing } from './JRCWidgetPresenceWrapperClosing';
import { WidgetIsPending } from './components/WidgetIsPending';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { RTProvider } from '../../RTProvider.component';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const JRCWidgetPresence = commonComponentsRegistry.getLazyComponent('WidgetPresence');
const ModalUserList = commonComponentsRegistry.getLazyComponent('ModalUserList');

export const CSSWidgetPresenceWrapper = styled.div`
    display: flex;
    flex: 1;
    position: relative;
    flex-direction: column;
`;

export const CSSWidgetPresenceState = styled.div`
    position: absolute;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
`;

export const CSSWidgetPresenceIsClosed = styled.div`
    flex: 1;
    display: flex;
    padding: ${(props) => props.theme.space.md}px;
    border: 1px dashed ${(props) => props.theme.color.orange};
    justify-content: center;
    align-items: center;
    border-radius: ${(props) => props.theme.space.sm}px;
    margin: ${(props) => props.theme.space.sm}px;
`;

export const JRCWidgetPresenceWrapper = ({
    widget,
    mode,
}: {
    widget: WidgetPresenceType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const { limit, limitValue, closing, isClosed } = widget.content;
    const { uniqid } = widget;
    const [response, setResponse] = useState<WidgetPresenceResponse>({
        userValue: -1,
        userAvailableList: [],
        userUnavailableList: [],
        userAvailableTotal: 0,
        userUnavailableTotal: 0,
    });
    const [isPendingResponse, setIsPendingResponse] = useState<boolean>(false);
    const [abortController] = useState<AbortController>(new AbortController());
    const [openUsersModal, setOpenUsersModal] = useState(false);
    const [usersListIndex, setUsersListIndex] = useState<number>();
    const [usersList, setUsersList] = useState<WidgetPresenceResponseUserList>();
    const ref = useRef<AbortController>(abortController);

    const uri = useAppSelector((state) => Widget.selectors.selectWidgetObjectUri(state, uniqid), shallowEqual);

    const allRt = useAppSelector((state) => Widget.selectors.selectAllWidgetRTObject(state, uniqid), shallowEqual);

    const handleClickAvailable = () => (response.userValue === 1 ? handleCancelRespond() : handleRespond(1));
    const handleClickUnavailable = () => (response.userValue === 0 ? handleCancelRespond() : handleRespond(0));

    const handleError = useCallback(
        () => dispatch(Toast.actions.error({ label: 'WIDGET_Presence_Error' })),
        [dispatch],
    );

    const canRespond = useMemo<boolean>(
        () => !(limit && limitValue && response.userAvailableTotal >= limitValue),
        [limit, limitValue, response.userAvailableTotal],
    );

    const lockedRespondHandler = useMemo(() => {
        return (closing && isClosed) || mode === 'edit';
    }, [closing, isClosed, mode]);

    const handleCancelRespond = useCallback(() => {
        if (!uri || lockedRespondHandler) return;
        if (!canRespond) {
            dispatch(Toast.actions.error({ label: 'WIDGET_Presence_Limit_Response_Error' }));
            return;
        }

        setIsPendingResponse(true);
        jApi.widget
            .presenceCancel({ uri, uniqid })
            .catch(handleError)
            .finally(() => setIsPendingResponse(false));
    }, [dispatch, uniqid, handleError, uri, canRespond, lockedRespondHandler]);

    const handleRespond = useCallback(
        (value: 1 | 0) => {
            if (!uri || lockedRespondHandler) return;

            setIsPendingResponse(true);
            jApi.widget
                .presenceRespond(
                    {
                        uri,
                        uniqid,
                        value,
                    },
                    ref.current.signal,
                )
                .catch(handleError)
                .finally(() => setIsPendingResponse(false));
        },
        [uri, uniqid, handleError, lockedRespondHandler],
    );

    const handleResponse = useCallback(() => {
        if (mode === 'edit' || !uri) return;
        setIsPendingResponse(true);
        jApi.widget
            .presenceGetResponse({ uri, uniqid }, ref.current.signal)
            .then((response) => {
                setResponse(response.result);
            })
            .catch((err) => !err.aborted && handleError())
            .finally(() => setIsPendingResponse(false));
    }, [uri, handleError, uniqid, mode]);

    const handleLoadUsersList = (index: number, page?: number) => {
        if (!uri) return;
        jApi.widget
            .presenceGetResponseUserList({ uri, uniqid, index, page, limit: 8 }, ref.current.signal)
            .then((response) => {
                setUsersList({
                    ...response.result,
                    data: [...response.result.data],
                    cnt: response.result.cnt,
                });
            })
            .catch((err) => {
                return !err.aborted && handleError;
            });
    };

    const handleReloadWidget = useCallback(() => {
        if (!uri) return;
        setIsPendingResponse(true);
        jApi.widget
            .getWidget<WidgetPresenceType>({ uri, uniqid })
            .then((response) => {
                dispatch(updateWidgetContent(uniqid, response.result));
            })
            .catch((err) => {
                return !err.aborted && handleError;
            })
            .finally(() => setIsPendingResponse(false));
    }, [dispatch, handleError, uri, uniqid]);

    useEffect(() => {
        const elm = ref.current;
        return () => {
            elm.abort();
        };
    }, []);

    useEffect(() => {
        handleResponse();
    }, [handleResponse]);

    useEffect(() => {
        if (!Array.isArray(allRt)) return;
        const lastRt = allRt.at(-1);
        if (!lastRt || !uri) return;

        if (lastRt.object.uri === uri && lastRt.uniqid === uniqid) {
            if (lastRt.fn === 'widget-presence-response') {
                handleResponse();
            }
            if (lastRt.fn === 'widget-update') {
                handleReloadWidget();
            }
        }
    }, [allRt, handleResponse, uniqid, uri, handleReloadWidget]);

    return (
        <RTProvider
            useRT={() => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useWidgetRT(widget.uniqid);
            }}>
            <CSSWidgetPresenceWrapper>
                <JRCWidgetPresence
                    limit={limit}
                    limitValue={limitValue}
                    userAvailableList={response.userAvailableList}
                    userUnavailableList={response.userUnavailableList}
                    userValue={response.userValue}
                    userAvailableTotal={response?.userAvailableTotal}
                    userUnavailableTotal={response?.userUnavailableTotal}
                    isClosed={closing && isClosed}
                    handleClickAvailable={handleClickAvailable}
                    handleClickUnavailable={handleClickUnavailable}
                    handleOpenUsersModal={(index) => {
                        setUsersList({
                            limit: 0,
                            page: 0,
                            data: [],
                            cnt: 0,
                        });
                        setUsersListIndex(index);
                        handleLoadUsersList(index);
                        setOpenUsersModal(true);
                    }}
                />
                {closing && isClosed && (
                    <CSSWidgetPresenceIsClosed>
                        {intl.formatMessage({ id: 'WIDGET_Presence_Is_Closed' })}
                    </CSSWidgetPresenceIsClosed>
                )}
                {closing && !isClosed && (
                    <WidgetPresenceWrapperClosing mode={mode} uniqid={widget.uniqid} content={widget.content} />
                )}
                {mode === 'view' && <WidgetFooterView uniqid={widget.uniqid} name={widget.name} mode={mode} />}
                {isPendingResponse && <WidgetIsPending />}
                <ModalUserList
                    open={openUsersModal}
                    usersList={usersList}
                    title={
                        usersListIndex === 1
                            ? intl.formatMessage({ id: 'WIDGET_Presence_On' })
                            : intl.formatMessage({ id: 'WIDGET_Presence_Off' })
                    }
                    setOpen={setOpenUsersModal}
                    onClose={() => {
                        setUsersList({
                            limit: 0,
                            page: 0,
                            data: [],
                            cnt: 0,
                        });
                    }}
                    handleLoadUsersList={(page) => {
                        handleLoadUsersList(usersListIndex ?? 0, page);
                    }}
                />
            </CSSWidgetPresenceWrapper>
        </RTProvider>
    );
};
